import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';

interface Permission {
  resource: string;
  action: string;
}

/**
 * Hook to check user permissions using database functions
 * Permissions are cached and automatically refreshed
 * 
 * @example
 * const { can, loading } = usePermissions();
 * if (can('recipes', 'create')) {
 *   return <CreateRecipeButton />;
 * }
 */
export const usePermissions = () => {
  const { user } = useAuth();

  const { data: permissions = [], isLoading } = useQuery({
    queryKey: queryKeys.user.permissions(user?.id),
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .rpc('get_my_permissions');

      if (error) {
        console.error('Failed to fetch permissions:', error);
        return [];
      }

      return (data || []) as Permission[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const can = (resource: string, action: string): boolean => {
    // Super admins can do everything
    if (user?.profile?.is_super_admin) {
      return true;
    }

    // Check if user has the specific permission
    return permissions.some(
      (p) => p.resource === resource && p.action === action
    );
  };

  const canAny = (checks: Array<{ resource: string; action: string }>): boolean => {
    return checks.some(({ resource, action }) => can(resource, action));
  };

  const canAll = (checks: Array<{ resource: string; action: string }>): boolean => {
    return checks.every(({ resource, action }) => can(resource, action));
  };

  return {
    can,
    canAny,
    canAll,
    loading: isLoading,
    permissions,
  };
};
